import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { PaymentTypeEnum } from '../../../../common/enums.js';
import { deliveryMethodMsg, eInvoiceMsg, emailInvoiceMsg, paperInvoiceMsg, t } from '../../../../common/i18n';
import { formatAddress } from '../../../../common/utils/accountUtils.js';
import {
  getBillingAccountBillReceiverName,
  getBillingAccountDeliveryMethod,
  getBillingAccountElectronicDetails,
  getBillingAccountPayerAddress,
  getBillingAccountPayerBusinessId,
  getBillingAccountPayerName,
} from '../../../../common/utils/billingAccountUtilsCoCo.js';
import { scrollTo } from '../../../../common/utils/browserUtils.js';
import { DeliveryType } from '../../../../generated/api/deliveryType.js';
import { BillingAccountDeliveryMethod } from '../../../../generated/api/models.js';
import { getDeliveryFields, getDeliveryMethodEntry } from '../../utils/placeOrderUtils.js';
import { StepError } from '../StepError.js';
import { CheckoutFooterCoCo } from '../common/CheckoutFooterCoCo.js';
import { ConfirmModal } from './ConfirmModal.js';
import { OrderInfo } from './OrderInfo.js';
import { SelectBillingAccountCoCo } from './SelectBillingAccountCoCo.js';
import { SelectPaymentMethodCoCo } from './SelectPaymentMethodCoCo.js';
import type { InvoicingInfo } from './OrderInfo.js';
import type { BillingAccountCocoCreationConfig } from './SelectBillingAccountCoCo.js';
import type { PaymentType } from '../../../../common/enums.js';
import type { BillingAccountCoco } from '../../../../generated/api/billingAccountCoco.js';
import type { PlaceOrderFormData } from '../../placeOrderTypes.js';
import type { JSX } from 'react';

import './PaymentStep.scss';

export interface PaymentStepProps {
  mdmId: string;
  contactInfo: string[];
  isCardPaymentPossible: boolean;
  isDeviceItemsOnly: boolean;
  onCreatingNewBillingAccountChange: (isCreating: boolean) => void;
  orderSummary: JSX.Element;
  isSubmittingOrder?: boolean;
  onSubmit: (billingAccountId: string) => void;
  onPrevious: () => void;
  billingAccountCreation?: BillingAccountCocoCreationConfig;
  onBillingAccountError?: (message: string) => void;
}

const PAYMENT_METHOD_ID = 'payment-method';

const getDeliveryInfoContent = (deliveryType?: DeliveryType, deliveryDescription?: string): JSX.Element | undefined => {
  if (deliveryType === DeliveryType.NONE) {
    return (
      <>
        <h4>{t.R2DK('Nothing to deliver')}</h4>
        <p>{t.FPAP('Your order contains products that do not require delivery.')}</p>
      </>
    );
  }
  if (deliveryDescription) {
    return (
      <>
        <h4>{t.GPPB('Delivery information')}</h4>
        <p>{deliveryDescription}</p>
      </>
    );
  }
  return undefined;
};

const getBillingAccountDeliveryDetails = (billingAccount: BillingAccountCoco): string => {
  switch (getBillingAccountDeliveryMethod(billingAccount)) {
    case BillingAccountDeliveryMethod.EMAIL:
      return getBillingAccountBillReceiverName(billingAccount);
    case BillingAccountDeliveryMethod.PAPER:
      return formatAddress(getBillingAccountPayerAddress(billingAccount)) ?? '';
    case BillingAccountDeliveryMethod.ELECTRONIC: {
      const details = getBillingAccountElectronicDetails(billingAccount);
      return [details.operator, details.address].filter(Boolean).join(' ');
    }
    default:
      return '';
  }
};

const getBillingAccountDeliveryDescription = (billingAccount: BillingAccountCoco): string => {
  switch (getBillingAccountDeliveryMethod(billingAccount)) {
    case BillingAccountDeliveryMethod.EMAIL:
      return t.NNV9(emailInvoiceMsg);
    case BillingAccountDeliveryMethod.PAPER:
      return t.TER4(paperInvoiceMsg);
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return t.IK1D(eInvoiceMsg);
    default:
      return '';
  }
};

const getInvoicingInfo = (paymentType: PaymentType, billingAccount?: BillingAccountCoco): InvoicingInfo | undefined => {
  if (paymentType !== PaymentTypeEnum.BILLING_ACCOUNT || !billingAccount) {
    return undefined;
  }
  return {
    left: (
      <>
        <h4>{t.HVS2('Selected billing account')}</h4>
        <p>
          {getBillingAccountPayerBusinessId(billingAccount)} {getBillingAccountPayerName(billingAccount)}
          <br />
          {getBillingAccountDeliveryDetails(billingAccount)}
        </p>
      </>
    ),
    right: (
      <>
        <h4>{t.XZH6(deliveryMethodMsg)}</h4>
        <p>{getBillingAccountDeliveryDescription(billingAccount)}</p>
      </>
    ),
  };
};

const getPaymentValidationError = (
  paymentType: PaymentType | undefined,
  billingAccountId: string | undefined
): string | undefined => {
  if (paymentType === PaymentTypeEnum.BILLING_ACCOUNT && !billingAccountId) {
    return t.PUM6('Please select a billing account');
  }
  if (paymentType === undefined) {
    return t.CBBN('Please select a payment method');
  }
  return undefined;
};

export const PaymentStepCoCo = ({
  mdmId,
  contactInfo,
  isCardPaymentPossible,
  isDeviceItemsOnly,
  onCreatingNewBillingAccountChange,
  orderSummary,
  isSubmittingOrder = false,
  onSubmit,
  onPrevious,
  billingAccountCreation,
  onBillingAccountError,
}: PaymentStepProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();

  const [selectedBillingAccount, setSelectedBillingAccount] = useState<BillingAccountCoco | undefined>();
  const [isCreatingNewBillingAccount, setIsCreatingNewBillingAccount] = useState(false);
  const [pendingPaymentType, setPendingPaymentType] = useState<PaymentType | undefined>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [paymentMethodKey, setPaymentMethodKey] = useState(0);
  const [paymentError, setPaymentError] = useState<string | undefined>();

  const delivery = watch('delivery');
  const paymentType = watch('payment.paymentType');
  const billingAccountId = watch('payment.billingAccountId');
  const deliveryType = watch('delivery.deliveryType');

  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType>(
    paymentType || PaymentTypeEnum.BILLING_ACCOUNT
  );

  const nothingToDeliver = deliveryType === DeliveryType.NONE;

  const resetCreatingNewState = () => {
    if (isCreatingNewBillingAccount) {
      setIsCreatingNewBillingAccount(false);
      onCreatingNewBillingAccountChange(false);
      scrollTo(0);
    }
  };

  const handleSubmit = () => {
    const currentPaymentType = watch('payment.paymentType');
    const currentBillingAccountId = watch('payment.billingAccountId');

    const validationError = getPaymentValidationError(currentPaymentType, currentBillingAccountId);
    if (validationError) {
      setPaymentError(validationError);
    } else {
      onSubmit(currentBillingAccountId!);
    }
    scrollTo(0);
  };

  const handleConfirmPaymentTypeChange = () => {
    setShowConfirmModal(false);
    setIsCreatingNewBillingAccount(false);
    onCreatingNewBillingAccountChange(false);
    if (pendingPaymentType) {
      setSelectedPaymentType(pendingPaymentType);
      setValue('payment.paymentType', pendingPaymentType);
      if (pendingPaymentType === PaymentTypeEnum.CARD) {
        setValue('payment.billingAccountId', '');
      }
      setPendingPaymentType(undefined);
      scrollTo(0);
    }
  };

  const handleCancelPaymentTypeChange = () => {
    setShowConfirmModal(false);
    setPendingPaymentType(undefined);
    setPaymentMethodKey(prev => prev + 1);
  };

  const handlePaymentMethodChange = (p: PaymentType) => {
    setPaymentError(undefined);

    if (isCreatingNewBillingAccount) {
      setPendingPaymentType(p);
      setShowConfirmModal(true);
      return;
    }

    setSelectedPaymentType(p);
    setValue('payment.paymentType', p);
    if (p === PaymentTypeEnum.CARD) {
      setValue('payment.billingAccountId', '');
      resetCreatingNewState();
    }
  };

  const handleBillingAccountChange = (ba: BillingAccountCoco) => {
    setPaymentError(undefined);

    if (ba.id) {
      setValue('payment.billingAccountId', ba.id);
      setSelectedBillingAccount(ba);
      resetCreatingNewState();
    }
  };

  const deliveryDescription = deliveryType ? getDeliveryMethodEntry(deliveryType)?.description : undefined;
  const deliveryFields =
    nothingToDeliver || !delivery?.deliveryAddress ? [] : getDeliveryFields(delivery.deliveryAddress);

  return (
    <div className="payment-step">
      <h1>{t.D9Q4('Payment')}</h1>
      <StepError
        className={dsClass.MARGIN_BOTTOM_4}
        errors={paymentError ? [{ text: paymentError, id: PAYMENT_METHOD_ID }] : []}
      />
      <div id={PAYMENT_METHOD_ID}>
        {isDeviceItemsOnly && (
          <SelectPaymentMethodCoCo
            key={paymentMethodKey}
            label={t.CBBN('Choose payment method')}
            paymentType={selectedPaymentType}
            onChange={handlePaymentMethodChange}
            isCardPaymentPossible={isCardPaymentPossible}
          />
        )}
        {selectedPaymentType === PaymentTypeEnum.BILLING_ACCOUNT && (
          <SelectBillingAccountCoCo
            error={paymentError}
            mdmId={mdmId}
            onChange={handleBillingAccountChange}
            onCreatingNewBillingAccountChange={isCreating => {
              setIsCreatingNewBillingAccount(isCreating);
              onCreatingNewBillingAccountChange(isCreating);
            }}
            billingAccountId={billingAccountId}
            createBillingAccount={billingAccountCreation}
            onBillingAccountError={onBillingAccountError}
            infoText={
              isCardPaymentPossible ? undefined : (
                <>
                  <h4>{t.PFPP('The Payment method for products with a monthly fee is always an invoice.')}</h4>
                  <p>{t.PSP3('Card payment is possible only for one-time fee devices and accessories.')}</p>
                </>
              )
            }
          />
        )}
      </div>
      {!isCreatingNewBillingAccount && (
        <>
          <OrderInfo
            mainTitle={t.FJW1('Review the order details')}
            orderContactInfo={{ fields: contactInfo }}
            deliveryInfo={{
              deliveryFields,
              deliveryInfo: getDeliveryInfoContent(deliveryType, deliveryDescription),
            }}
            invoicingInfo={getInvoicingInfo(selectedPaymentType, selectedBillingAccount)}
            showBackgroundColor={true}
          />
          <div className={dsClass.PADDING_BOTTOM_2} />
          {orderSummary}
          <CheckoutFooterCoCo onSubmit={handleSubmit} onPrevious={onPrevious} disableForward={isSubmittingOrder} />
        </>
      )}
      {showConfirmModal && (
        <ConfirmModal onStay={handleCancelPaymentTypeChange} onContinue={handleConfirmPaymentTypeChange} />
      )}
    </div>
  );
};
